import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { AuthService } from '../services/auth/auth.service';
import { AuthResponse } from '../interfaces/auth.interfaces';
import { TokenExpirationMonitorService } from '../services/auth/token-expiration-monitor.service';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

function addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
  return request.clone({
    setHeaders: {
      'Authorization': `Bearer ${token}`
    }
  });
}

function isPublicAuthRequest(request: HttpRequest<any>): boolean {
  return request.url.includes('/auth/validate-recover-token') ||
    request.url.includes('/auth/generate-recover-token') ||
    request.url.includes('/auth/login');
}

function isRefreshRequest(request: HttpRequest<any>): boolean {
  return request.url.includes('/auth/refresh');
}

function handle401Error(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  tokenMonitor: TokenExpirationMonitorService
): Observable<HttpEvent<any>> {
  // Verificar si el token ya expiró completamente
  const expirationTime = authService.getSessionExpirationTime();
  if (expirationTime && Date.now() >= expirationTime) {
    // Token completamente expirado, no intentar refrescar
    isRefreshing = false;
    authService.logout();
    return throwError(() => new Error('Token expired'));
  }

  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap((authResponse: AuthResponse) => {
        isRefreshing = false;
        refreshTokenSubject.next(authResponse.access_token);
        tokenMonitor.startMonitoring();
        const bc = new BroadcastChannel('precredit_session_channel');
        bc.postMessage({ type: 'TOKEN_REFRESHED' });
        bc.close();
        
        return next(addTokenToRequest(request, authResponse.access_token));
      }),
      catchError((err) => {
        isRefreshing = false;
        if (!authService.isLoggingOut()) {
          const reason = err?.headers?.get?.('X-Session-Expired-Reason') || err?.error?.expiredReason || '';
          authService.forceLogoutWithReason(reason);
        }
        return throwError(() => err);
      })
    );
  } else {
    return refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(newAccessToken => {
        return next(addTokenToRequest(request, newAccessToken!));
      })
    );
  }
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {

  const authService = inject(AuthService);
  const tokenMonitor = inject(TokenExpirationMonitorService);
  const accessToken = authService.getAccessToken();
  if (accessToken) {
    req = addTokenToRequest(req, accessToken);
  }

  if (!isPublicAuthRequest(req) && !isRefreshRequest(req) && accessToken) {
    tokenMonitor.resetIdleTimer();
  }

  return next(req).pipe(
    catchError(error => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        if (isPublicAuthRequest(req)) {
          return throwError(() => error);
        }
        if (accessToken) {
          return handle401Error(req, next, authService, tokenMonitor);
        }
      }
      return throwError(() => error);
    })
  );
};